import React, { useMemo } from 'react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import { Box, CircularProgress } from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import COLORS from 'theme/colors';
import { formatSlotLocation, stripOfficeOffset } from './mappers';
import { AvailabilitySlot } from './types';

dayjs.extend(utc);
dayjs.extend(timezone);

// Calendar setup (plugins, view, header layout, time range) mirrors pages/Admin/AppointmentCalendar.tsx.

// Matches the backend's OFFICE_TIMEZONE (SelfSchedulingService) - a slot can't be booked once
// it's passed, so the calendar shouldn't let a patient navigate to a week before today
// office-local either. Computed as a literal UTC midnight (see stripOfficeOffset elsewhere in
// this feature) so it lines up with this calendar's timeZone='UTC' mode.
const officeTodayUtcLiteral = () => new Date(`${dayjs().tz('America/Chicago').format('YYYY-MM-DD')}T00:00:00Z`);

// Matches the backend's PREVIEW_WINDOW_DAYS (SelfSchedulingService) - the provider list's own
// preview never looks further than this, so the calendar shouldn't let a patient navigate past
// it either. +1 day since validRange's end is exclusive - day 90 itself should stay reachable,
// only day 91+ (an entirely-beyond-the-window week) gets disabled.
const PREVIEW_WINDOW_DAYS = 90;
const officeMaxBookableUtcLiteral = () =>
  new Date(`${dayjs().tz('America/Chicago').add(PREVIEW_WINDOW_DAYS + 1, 'day').format('YYYY-MM-DD')}T00:00:00Z`);

interface AvailabilityCalendarProps {
  availability: AvailabilitySlot[];
  onSelectSlot: (slot: AvailabilitySlot) => void;
  // Fired (with an ISO yyyy-MM-dd) whenever the visible week changes, including via the
  // prev/next buttons, so the parent can fetch just that week instead of the whole range.
  onWeekChange: (weekStartIso: string) => void;
  initialDate?: Date;
  // Shows a loading overlay without clearing the currently-displayed (possibly stale) week's
  // events, so the calendar doesn't flash empty while a new week's slots are being fetched.
  loading?: boolean;
}

const AvailabilityCalendar = ({
  availability,
  onSelectSlot,
  onWeekChange,
  initialDate,
  loading,
}: AvailabilityCalendarProps) => {
  const validRangeStart = useMemo(() => officeTodayUtcLiteral(), []);
  const validRangeEnd = useMemo(() => officeMaxBookableUtcLiteral(), []);

  const events = useMemo(
    () =>
      availability.map((slot) => ({
        id: slot.id,
        title: formatSlotLocation(slot) || 'Available',
        // FullCalendar parses an embedded offset and converts the resulting instant to true
        // UTC for display (timeZone='UTC' below) rather than showing it literally, so the
        // offset has to be stripped here too (see stripOfficeOffset) - otherwise a 10:40am
        // Chicago slot renders as 3:40pm (its real UTC equivalent) instead of 10:40am.
        start: stripOfficeOffset(slot.start),
        end: stripOfficeOffset(slot.end),
        backgroundColor: '#E6F4EA',
        // MM-1175: dark green text and border so short slots (e.g. 20 minutes) stay readable on the
        // light green box, on mobile too (about 7:1 contrast instead of 2.3:1)
        borderColor: COLORS.Success800,
        textColor: COLORS.Success800,
        extendedProps: { slot },
      })),
    [availability]
  );

  return (
    <Box
      sx={{
        position: 'relative',
        backgroundColor: COLORS.White,
        border: `1px solid ${COLORS.Gray300}`,
        borderRadius: '12px',
        p: 2,
        // MM-1175: semi-bold slot text, easier to read in short slots
        '& .fc-event-main': { fontWeight: 600 },
      }}
    >
      {loading ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            borderRadius: '12px',
            zIndex: 1,
          }}
        >
          <CircularProgress size={32} />
        </Box>
      ) : null}
      <FullCalendar
        // Slot start/end are office-local wall-clock time (e.g. "...T10:40:00-05:00" for a
        // 10:40am Chicago appointment) - a patient needs to see that literal office time
        // regardless of their own browser's timezone (an in-person visit happens at 10:40am
        // Chicago time no matter where the patient is when they booked it). The events array
        // above already strips each event's offset (stripOfficeOffset) - without an offset to
        // convert from, timeZone='UTC' then just renders the string's digits as given. Using
        // a named zone (e.g. the browser's own IANA zone) instead would still need
        // @fullcalendar/moment-timezone or @fullcalendar/luxon3, neither of which is installed.
        timeZone='UTC'
        // Disables (grays out, same not-allowed hover as the other end) the "prev" button once
        // going further back would show a week before today - a past slot can't be booked
        // anyway (see the backend's matching already-started-slot exclusion) - and the "next"
        // button once going further forward would show a week entirely past the 90-day
        // preview window, matching the provider list's own preview horizon.
        validRange={{ start: validRangeStart, end: validRangeEnd }}
        // Week grid shows 8:00 AM - 7:00 PM (office time) for every provider, location and
        // profile; slots outside this range are not drawn.
        slotMinTime='08:00:00'
        slotMaxTime='19:00:00'
        expandRows
        height={700}
        plugins={[timeGridPlugin, interactionPlugin]}
        initialView='timeGridWeek'
        initialDate={initialDate}
        headerToolbar={{
          left: 'prev',
          center: 'title',
          right: 'next',
        }}
        events={events}
        eventDisplay='block'
        eventClick={(info) => onSelectSlot(info.event.extendedProps.slot as AvailabilitySlot)}
        datesSet={(info) => onWeekChange(info.start.toISOString().slice(0, 10))}
      />
    </Box>
  );
};

export default AvailabilityCalendar;
